import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import type { PublicTrendingResponse, PublicTrendingVideo } from '../../lib/api/public'
import { getTrendingPublic } from '../../lib/api/public'
import { getWatchHistory, getDiscover, type WatchHistoryEntry } from '../../lib/api/user-content'
import { getPublicFilm } from '../../lib/api/public-films.client'
import type { Video } from '../../lib/types/api'
import { Play, Sparkles, ArrowRight, LayoutDashboard, Download, Menu, X, Megaphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { VerticalFilmPlaceholder } from '@/components/vertical-film-placeholder'

/** One entry per series (latest watched episode), for "Pick up where you left off". */
function groupWatchHistoryBySeries(items: WatchHistoryEntry[], maxItems: number): WatchHistoryEntry[] {
  const byFilm = new Map<string, WatchHistoryEntry>()
  for (const entry of items) {
    const filmId = entry.filmId || (entry as unknown as { film_id?: string }).film_id
    if (!filmId) continue
    const existing = byFilm.get(filmId)
    if (!existing || new Date(entry.watchedAt) > new Date(existing.watchedAt)) {
      byFilm.set(filmId, entry)
    }
  }
  return Array.from(byFilm.values())
    .sort((a, b) => new Date(b.watchedAt).getTime() - new Date(a.watchedAt).getTime())
    .slice(0, maxItems)
}

/** Normalize thumbnail: backend feed uses thumbnailUrl (camelCase), public API uses thumbnail_url. */
function getThumbnailUrl(v: { thumbnail_url?: string; thumbnailUrl?: string }): string | null | undefined {
  return (v as { thumbnail_url?: string }).thumbnail_url ?? (v as { thumbnailUrl?: string }).thumbnailUrl
}

const navLinks = [
  { to: '/originals', label: 'Originals' },
  { to: '/billing', label: 'Billing' },
  { to: '/advertise', label: 'Advertise' },
  { to: '/press', label: 'Press' },
  { to: '/settings', label: 'Settings' },
] as const

export function Home() {
  const { user, logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [watchHistoryItems, setWatchHistoryItems] = useState<WatchHistoryEntry[]>([])
  const [validWatchHistoryBySeries, setValidWatchHistoryBySeries] = useState<WatchHistoryEntry[]>([])
  const [discoverVideos, setDiscoverVideos] = useState<Video[]>([])
  const [trendingForWelcome, setTrendingForWelcome] = useState<PublicTrendingVideo[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [loadingDiscover, setLoadingDiscover] = useState(false)
  const [loadingTrending, setLoadingTrending] = useState(false)

  /** One card per series (latest watched), for "Pick up where you left off". */
  const watchHistoryBySeries = useMemo(
    () => groupWatchHistoryBySeries(watchHistoryItems, 6),
    [watchHistoryItems]
  )

  useEffect(() => {
    let cancelled = false
    if (!watchHistoryBySeries.length) {
      setValidWatchHistoryBySeries([])
      return
    }

    const validateShows = async () => {
      const results = await Promise.all(
        watchHistoryBySeries.map(async (entry) => {
          const filmId = entry.filmId || (entry as unknown as { film_id?: string }).film_id
          if (!filmId) {
            return { entry, keep: true }
          }
          try {
            await getPublicFilm(filmId)
            return { entry, keep: true }
          } catch (err) {
            if (err instanceof Error && err.message === 'Show not found') {
              return { entry, keep: false }
            }
            return { entry, keep: true }
          }
        })
      )

      if (!cancelled) {
        setValidWatchHistoryBySeries(results.filter((r) => r.keep).map((r) => r.entry))
      }
    }

    void validateShows()

    return () => {
      cancelled = true
    }
  }, [watchHistoryBySeries])

  useEffect(() => {
    if (!user) return
    setLoadingHistory(true)
    setLoadingDiscover(true)
    setLoadingTrending(true)
    getWatchHistory({ limit: 20 })
      .then((res) => setWatchHistoryItems(res.items || []))
      .catch(() => setWatchHistoryItems([]))
      .finally(() => setLoadingHistory(false))
    getDiscover(6)
      .then((res) => setDiscoverVideos(res.videos || []))
      .catch(() => setDiscoverVideos([]))
      .finally(() => setLoadingDiscover(false))
    getTrendingPublic(6)
      .then((res: PublicTrendingResponse) => {
        const valid = (res.videos || []).filter((v) => v?.id != null && String(v.id).trim() !== '')
        setTrendingForWelcome(valid)
      })
      .catch(() => setTrendingForWelcome([]))
      .finally(() => setLoadingTrending(false))
  }, [user])

  if (!user) return null

  const displayName = user.name?.trim() || user.email?.split('@')[0] || 'there'
  const isAdmin = (user as { isAdmin?: boolean }).isAdmin
  // TODO: replace with user.isAdvertiser once the backend exposes that flag
  const isAdvertiser = isAdmin

  const renderVideoCard = (
    id: string | number,
    title: string,
    thumbnailUrl: string | null | undefined
  ) => (
    <Link
      key={id}
      to={`/shows/${id}`}
      className="group relative aspect-[1/2] min-w-[140px] w-[140px] sm:min-w-[160px] sm:w-[160px] rounded-xl overflow-hidden bg-gray-900 hover:scale-[1.02] transition-transform flex-shrink-0"
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          className="w-full h-full object-contain"
        />
      ) : (
        <VerticalFilmPlaceholder title={title} className="w-full h-full" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-bold text-sm line-clamp-2">{title}</h3>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-14 h-14 rounded-full bg-[#D72638] flex items-center justify-center shadow-[0_0_24px_rgba(215,38,56,0.6)]">
          <Play className="w-7 h-7 text-white fill-white ml-0.5" />
        </div>
      </div>
    </Link>
  )

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-3 bg-black/90 backdrop-blur-sm border-b border-gray-800/50">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/img/logo.png" alt="Binyuma TV" className="w-9 h-9 sm:w-10 sm:h-10 object-contain opacity-90" />
        </Link>
        <nav className="hidden md:flex items-center gap-5 lg:gap-6">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors"
            >
              {label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => logout()}
            className="text-sm text-gray-400 hover:text-[#D72638] transition-colors"
          >
            Log out
          </button>
        </nav>
        <div className="md:hidden flex items-center">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="p-2 text-gray-400 hover:text-white rounded-lg transition-colors"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-20 pt-14 pb-6 px-4 bg-black/95 backdrop-blur-sm overflow-y-auto">
          <div className="flex flex-col gap-1">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
                className="py-3 px-4 rounded-lg text-gray-300 hover:bg-white/5 hover:text-[#D4AF37] transition-colors"
              >
                {label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false)
                logout()
              }}
              className="py-3 px-4 rounded-lg text-left text-gray-300 hover:bg-white/5 hover:text-[#D72638] transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0,rgba(212,175,55,0.12),transparent_50%),radial-gradient(circle_at_80%_100%,rgba(74,31,184,0.2),transparent_50%)]" />
      <div className="relative min-h-screen pb-20">
        <div className="max-w-6xl mx-auto px-6 pt-8 md:pt-12">
          <div className="text-center mb-12">
            <img
              src="/img/logo.png"
              alt="Binyuma TV"
              className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-4 object-contain opacity-90"
            />
            <h1 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent mb-2">
              Welcome back, {displayName}
            </h1>
            <p className="text-lg text-gray-400 mb-6">
              Your next watch is ready.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link to="/download" className="inline-block">
                <Button
                  size="lg"
                  className="bg-[#D72638] hover:bg-[#D72638]/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_30px_rgba(215,38,56,0.5)]"
                >
                  Go to app
                  <ArrowRight className="ml-2 w-5 h-5 inline-block" />
                </Button>
              </Link>
              <a href="/download" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
                <Download className="w-4 h-4" />
                Install for best experience
              </a>
            </div>
            <p className="mt-3 text-xs text-gray-500 max-w-md mx-auto">
              The full app experience is coming soon. Install the web app for the best experience on your device.
            </p>
          </div>

          {validWatchHistoryBySeries.length > 0 && (
            <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Pick up where you left off</h2>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                {validWatchHistoryBySeries.map((entry) => {
                  const filmId = entry.filmId || (entry as unknown as { film_id?: string }).film_id
                  const filmTitle =
                    entry.filmTitle || (entry as unknown as { film_title?: string }).film_title
                  const epNum = entry.episodeNumber ?? 1
                  const epTitle = entry.episodeTitle || ''
                  const title =
                    filmTitle
                      ? (epTitle ? `${filmTitle} — Ep ${epNum}: ${epTitle}` : `${filmTitle} — Ep ${epNum}`)
                      : epTitle || 'Continue watching'
                  const thumb =
                    entry.coverImage || (entry as unknown as { cover_image?: string }).cover_image
                  return renderVideoCard(filmId ?? entry.id, title, thumb || undefined)
                })}
              </div>
            </section>
          )}

          {loadingDiscover && discoverVideos.length === 0 ? (
            <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Recommended for you</h2>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="aspect-[1/2] min-w-[140px] w-[140px] sm:min-w-[160px] sm:w-[160px] rounded-xl bg-gray-800/80 animate-pulse flex-shrink-0"
                  />
                ))}
              </div>
            </section>
          ) : discoverVideos.length > 0 ? (
            <section className="mb-12">
              <h2 className="text-xl font-semibold mb-4">Recommended for you</h2>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                {discoverVideos.map((v) =>
                  renderVideoCard(v.id, v.title, getThumbnailUrl(v))
                )}
              </div>
            </section>
          ) : null}

          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-4">Trending on Binyuma</h2>
            {loadingTrending && trendingForWelcome.length === 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="aspect-[1/2] min-w-[140px] w-[140px] sm:min-w-[160px] sm:w-[160px] rounded-xl bg-gray-800/80 animate-pulse flex-shrink-0" />
                ))}
              </div>
            ) : trendingForWelcome.length > 0 ? (
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin">
                {trendingForWelcome.map((v) =>
                  renderVideoCard(v.id, v.title, getThumbnailUrl(v))
                )}
              </div>
            ) : (
              <Link
                to="/originals"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline font-medium"
              >
                Browse originals
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </section>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 text-left max-w-4xl mx-auto mt-16">
            <Link
              to="/creator"
              className="group rounded-xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent px-4 py-4 hover:border-[#D4AF37] hover:bg-[#D4AF37]/15 transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D4AF37]/20">
                  <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#D4AF37]">Become a creator</p>
                  <p className="text-xs text-gray-400">
                    Access the Binyuma Studio to upload and manage your shows.
                  </p>
                </div>
              </div>
              <p className="mt-2 inline-flex items-center text-xs font-medium text-[#D4AF37] group-hover:translate-x-0.5 transition-transform">
                Open creator studio
                <ArrowRight className="ml-1 h-3 w-3" />
              </p>
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="group rounded-xl border border-[#4A1FB8]/50 bg-gradient-to-br from-[#4A1FB8]/15 to-transparent px-4 py-4 hover:border-[#4A1FB8] hover:bg-[#4A1FB8]/20 transition-all"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#4A1FB8]/25">
                    <LayoutDashboard className="h-5 w-5 text-[#b19cff]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#b19cff]">Admin dashboard</p>
                    <p className="text-xs text-gray-400">
                      Review content, users, and platform activity.
                    </p>
                  </div>
                </div>
                <p className="mt-2 inline-flex items-center text-xs font-medium text-[#b19cff] group-hover:translate-x-0.5 transition-transform">
                  Go to admin tools
                  <ArrowRight className="ml-1 h-3 w-3" />
                </p>
              </Link>
            )}
            {isAdvertiser && (
              <Link
                to="/advertiser"
                className="group rounded-xl border border-[#D72638]/40 bg-gradient-to-br from-[#D72638]/10 to-transparent px-4 py-4 hover:border-[#D72638] hover:bg-[#D72638]/15 transition-all"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D72638]/20">
                    <Megaphone className="h-5 w-5 text-[#D72638]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#D72638]">Advertiser portal</p>
                    <p className="text-xs text-gray-400">
                      Manage sponsors, ad creatives, and placements.
                    </p>
                  </div>
                </div>
                <p className="mt-2 inline-flex items-center text-xs font-medium text-[#D72638] group-hover:translate-x-0.5 transition-transform">
                  Open ads portal
                  <ArrowRight className="ml-1 h-3 w-3" />
                </p>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
